import { useMutation } from "@tanstack/react-query";

import { API_ROUTES } from "../api/ApiRoutes";
import { getPrivateInstance } from "./_privateInstance";
import { extractDownloadData } from "./presignedDownload";

const LIFECYCLE_DOWNLOAD_ROUTES = {
  pending: API_ROUTES.GET_PENDING_TRANSACTIONS_DOWNLOAD,
  "in-progress": API_ROUTES.GET_IN_PROGRESS_TRANSACTIONS_DOWNLOAD,
  processed: API_ROUTES.GET_PROCESSED_TRANSACTIONS_DOWNLOAD,
};

/**
 * @typedef {"pending" | "in-progress" | "processed"} WithdrawalReviewLifecycle
 */

/**
 * @typedef {Object} DownloadWithdrawalReviewPayload
 * @property {WithdrawalReviewLifecycle} lifecycle
 * @property {string} start_date
 * @property {string} end_date
 * @property {string} [subregion]
 * @property {string} [method]
 */

/**
 * @typedef {Object} DownloadWithdrawalReviewResponse
 * @property {string} download_url
 * @property {string} file_name
 * @property {number} [row_count]
 * @property {boolean} [truncated]
 */

/**
 * GET server-side CSV export for Withdrawal Review (presigned S3 URL).
 * @param {DownloadWithdrawalReviewPayload} payload
 * @returns {Promise<DownloadWithdrawalReviewResponse | null>}
 */
export async function downloadWithdrawalReview({
  lifecycle,
  start_date,
  end_date,
  subregion,
  method,
}) {
  const route = LIFECYCLE_DOWNLOAD_ROUTES[lifecycle];
  if (!route) {
    throw new Error(`Invalid withdrawal review lifecycle: ${lifecycle}`);
  }

  const instance = getPrivateInstance();

  const params = {
    start_date,
    end_date,
    ...(subregion && { subregion }),
    ...(method && { method }),
  };

  return extractDownloadData(await instance.get(route, { params }));
}

export const useDownloadWithdrawalReview = () =>
  useMutation({
    mutationKey: ["WITHDRAWAL_REVIEW_DOWNLOAD"],
    mutationFn: downloadWithdrawalReview,
  });
